import {match} from 'ts-pattern';

import {useEffect} from 'react';
import {useDispatch} from 'react-redux';

import {isNotNil} from 'ramda';

import {omneticApi} from '@dms/api/base';
import {chatAgentApi} from '@dms/api/core';

import {onSendEvent, Type} from '@chat/shared/graphql';
import {useTypingState} from '@chat/ui';

import {chatAppSync} from '../utils/chatAppSync';

interface UseChatSubscribeProps {
  conversationId?: string;
  currentUserId?: string;
  businessCaseId?: string;
}

export function useChatSubscribe(props: UseChatSubscribeProps) {
  const {conversationId, currentUserId, businessCaseId} = props;

  const dispatch = useDispatch();
  const [refetchChatHistory] = chatAgentApi.useLazyGetAgentEventsQuery();
  const {isTyping, acknowledgeTyping, resetTypingManual} = useTypingState();

  useEffect(
    function subscribeToChatEvents() {
      if (!conversationId || !currentUserId) {
        return;
      }

      const chatSubscription = chatAppSync
        .graphql({
          query: onSendEvent,
          variables: {conversationId},
        })
        .subscribe({
          next: (event) => {
            if (!event.data) {
              return;
            }

            match(event.data.onSendEvent.type)
              .with(Type.TYPING, () => {
                if (event.data.onSendEvent.userId !== currentUserId) {
                  acknowledgeTyping();
                }
              })
              .with(Type.READ, () => refetchChatHistory({conversationId}))
              .otherwise(() => {
                refetchChatHistory({conversationId});
                resetTypingManual();
                // Also fires for the agent's own sent messages — one extra refetch, accepted.
                if (isNotNil(businessCaseId)) {
                  dispatch(
                    omneticApi.util.invalidateTags([
                      {type: 'BusinessCaseActivity', id: businessCaseId},
                      {type: 'BusinessCaseDetail', id: businessCaseId},
                    ])
                  );
                }
              });
          },
        });

      return () => {
        chatSubscription.unsubscribe();
      };
    },
    [
      refetchChatHistory,
      conversationId,
      acknowledgeTyping,
      resetTypingManual,
      currentUserId,
      businessCaseId,
      dispatch,
    ]
  );

  return {
    isTyping,
  };
}
